import type { BulkApproveGroupInput } from '../types/bulk-approve.js';
import type { WorkItemSummary } from '../types/workflow.js';
import { isLessonDraft, isPlacedLesson, isStepAppend } from './workItems.js';

/**
 * Turning "the reviewer clicked Approve cluster on these rows" into a bulk-approve request.
 *
 * ## Why this exists
 *
 * `POST /api/v1/queue/bulk-approve` takes GROUPS, and cross-checks every declared roster against
 * `WorkItem.atomicGroup.memberWorkItemIds` — the membership DERIVED by the importer and frozen
 * (`queue.service.ts` `findRosterDisagreements`). The queue UI used to submit every item as its own
 * singleton, so an item in a real multi-member group declared a roster of one, disagreed with the
 * frozen roster, and was refused `group_mismatch`. **A grouped item could not be approved through
 * the bulk path at all** — 286 of ~926 production items, in 110 groups (`L-0339`).
 *
 * ## The rule this module enforces, and why it is not "just send the roster"
 *
 * Declaring the full roster fixes the refusal and opens a worse hole: the server approves every
 * eligible member it is handed, under the signed-in reviewer's identity. A cluster button that
 * quietly approved a pending sibling from a lesson the reviewer never opened would write exactly
 * the false attribution AD-38 exists to prevent, through a different door. (Found by cross-model
 * review of this design, before it was built.)
 *
 * So a request separates the two meanings that were conflated:
 *
 * - `workItemIds` — the COMPLETE frozen roster, for validation.
 * - `approveWorkItemIds` — the items the reviewer actually had on screen and is approving.
 *
 * and a group is only submitted at all when every roster member outside that intent set is in a
 * status that already satisfies closure. Anything else is WITHHELD with a named reason, because a
 * refusal the reviewer can read beats a server error he cannot act on.
 */

/**
 * Mirrors `BULK_MACHINE_AUTHORITY_REQUIRED_ABOVE` in `bulk-approve.types.ts`: above this many
 * declared items, `apply` requires an AD-38 machine authority, which a human reviewer must never
 * be given. The client refuses locally so the reviewer gets a sentence instead of a 400 — and it
 * NEVER splits the request to get under the line, because splitting is how a roster gets divided
 * and how the provenance threshold gets evaded.
 */
export const BULK_APPROVE_HUMAN_ITEM_LIMIT = 50;

/**
 * Statuses that satisfy a group without needing a fresh decision.
 *
 * EXACTLY the server's set (`classifyForBulkApprove` answers `already_approved` for these two and
 * `wrong_status` for every other status, `executing` and `executed` included). This list previously
 * also carried `executing` and `executed`, which made the client submit groups the server would
 * deterministically block — a refusal after the click instead of a sentence before it.
 */
const ALREADY_DECIDED: ReadonlySet<string> = new Set(['approved', 'approved_with_edit']);

/**
 * The wire `groupId` is a correlation label in the report, and the endpoint caps it at 128
 * characters and refuses duplicates. A readable identity triple usually fits; when it does not, a
 * positional id is used rather than failing the whole request over a long namespace.
 */
const MAX_WIRE_GROUP_ID_LENGTH = 128;

/**
 * The partition key, and it is INJECTIVE — deliberately not the readable label.
 *
 * `/` is a legal character in an imported group identifier (`GROUP_IDENTIFIER` in
 * `import-validator.ts`), so joining the triple with `/` collides: `(a, b, c/d)` and `(a, b/c, d)`
 * produce the same string. The server has the same rule and the same reason — `atomicGroupKey`
 * encodes canonical JSON precisely because ad hoc separators were forgeable. Mirrored here rather
 * than shared, because the two run in different packages; the encodings need not be byte-identical,
 * only both injective.
 */
function partitionKey(courseKey: string, groupNamespace: string, groupId: string): string {
  return JSON.stringify([courseKey, groupNamespace, groupId]);
}

export type WithheldReason =
  /** A roster member is pending and the reviewer did not have it on screen. */
  | 'unreviewed_member'
  /** A roster member could not be resolved at all — absence is never taken as approval. */
  | 'unresolved_member'
  /** A roster member is cancelled or otherwise unapprovable, so the server would block the group. */
  | 'member_not_approvable'
  /** The group carries a repo B key-lane spec, so it can never be composed into a wave. */
  | 'external_members'
  /** The frozen roster does not list the item carrying it — an import defect, not a review one. */
  | 'roster_inconsistent';

export interface WithheldGroup {
  /** `courseKey/groupNamespace/groupId` — the identity triple, readable. */
  label: string;
  reason: WithheldReason;
  /** Members of this group the reviewer asked to approve, and which were therefore not approved. */
  workItemIds: string[];
  /** The members that caused the withholding — what the reviewer has to go and look at. */
  blockingWorkItemIds: string[];
}

export interface ClusterApprovalPlan {
  groups: BulkApproveGroupInput[];
  withheld: WithheldGroup[];
  /** Unique ids across every declared roster. The server's 50-item human threshold counts THESE. */
  submittedItemCount: number;
  /** Unique ids that would actually be approved. */
  approveItemCount: number;
}

export function atomicGroupLabel(item: WorkItemSummary): string | null {
  const group = item.atomicGroup;
  return group ? `${item.courseKey}/${group.groupNamespace}/${group.groupId}` : null;
}

/**
 * Total members of the group, INCLUDING external ones. A group of one work item plus one key-lane
 * spec is a group of two: closure is judged over both, and only one of them is observable here.
 */
export function atomicGroupSize(item: WorkItemSummary): number {
  const group = item.atomicGroup;
  if (!group) {
    return 0;
  }
  return group.memberWorkItemIds.length + (group.externalMembers?.length ?? 0);
}

export function isInMultiMemberGroup(item: WorkItemSummary): boolean {
  return atomicGroupSize(item) > 1;
}

/**
 * Roster members of `candidates` that are not already in `resolved`.
 *
 * The queue list is filtered — by status, by section, and locally by task type and search — so a
 * sibling that is genuinely approved may simply not be loaded. Withholding on that would refuse
 * exactly the groups a reviewer is halfway through. The caller reads these ids
 * (`GET /api/v1/queue/items/:id`, a safe method and therefore not throttled) and plans with them
 * in hand.
 */
export function rosterMembersToResolve(
  candidates: readonly WorkItemSummary[],
  resolved: ReadonlyMap<string, WorkItemSummary>,
): string[] {
  const wanted = new Set<string>();
  for (const candidate of candidates) {
    for (const memberId of candidate.atomicGroup?.memberWorkItemIds ?? []) {
      if (!resolved.has(memberId)) {
        wanted.add(memberId);
      }
    }
  }
  return [...wanted];
}

export function planClusterApproval(
  candidates: readonly WorkItemSummary[],
  resolved: ReadonlyMap<string, WorkItemSummary>,
): ClusterApprovalPlan {
  const groups: BulkApproveGroupInput[] = [];
  const withheld: WithheldGroup[] = [];

  // Lessons and appends require individual preview review. Exclude them here too so another
  // caller cannot bypass the dashboard filter. Then dedupe by id for the server contract.
  const seen = new Set<string>();
  const unique: WorkItemSummary[] = [];
  for (const candidate of candidates) {
    if (!seen.has(candidate.workItemId) && !isLessonDraft(candidate) && !isStepAppend(candidate) && !isPlacedLesson(candidate)) {
      seen.add(candidate.workItemId);
      unique.push(candidate);
    }
  }

  // Partition by the identity TRIPLE, canonically encoded. Two items sharing a bare `groupId` under
  // different namespaces, or across courses, are different groups; merging them would declare a
  // roster nobody froze.
  const partitions = new Map<string, { label: string; roster: string[]; external: number; intent: string[] }>();
  for (const candidate of unique) {
    const group = candidate.atomicGroup;
    if (!group) {
      // Unchanged from the original client: an independent item is a group of one, so one item's
      // problem never blocks another.
      groups.push({
        groupId: candidate.workItemId,
        workItemIds: [candidate.workItemId],
        approveWorkItemIds: [candidate.workItemId],
      });
      continue;
    }

    const key = partitionKey(candidate.courseKey, group.groupNamespace, group.groupId);
    const existing = partitions.get(key);
    if (existing) {
      existing.intent.push(candidate.workItemId);
      // UNION the external members across the group's members rather than keeping the first
      // candidate's. Import validates `member_count` against the union of external refs but writes
      // each item's OWN `external_members`, so two members of one group can legitimately carry
      // different sets — and taking the first would make the verdict depend on which row the
      // reviewer's screen happened to list first. Found by cross-model review of this diff.
      existing.external = Math.max(existing.external, group.externalMembers?.length ?? 0);
      continue;
    }
    partitions.set(key, {
      label: `${candidate.courseKey}/${group.groupNamespace}/${group.groupId}`,
      roster: [...group.memberWorkItemIds],
      external: group.externalMembers?.length ?? 0,
      intent: [candidate.workItemId],
    });
  }

  for (const partition of partitions.values()) {
    const intent = new Set(partition.intent);

    // A roster that does not list the item carrying it cannot be repaired by any review decision.
    const orphans = partition.intent.filter((id) => !partition.roster.includes(id));
    if (orphans.length > 0) {
      withheld.push({
        label: partition.label,
        reason: 'roster_inconsistent',
        workItemIds: partition.intent,
        blockingWorkItemIds: orphans,
      });
      continue;
    }

    // Approving a group that can never be composed into a wave spends the reviewer's time on an
    // item that will sit approved and unreleasable. `assertAtomicGroupsClosed` refuses it in both
    // directions and `createWave` has no acknowledgement path for it.
    if (partition.external > 0) {
      withheld.push({
        label: partition.label,
        reason: 'external_members',
        workItemIds: partition.intent,
        blockingWorkItemIds: [],
      });
      continue;
    }

    const unreviewed: string[] = [];
    const unresolved: string[] = [];
    const notApprovable: string[] = [];
    for (const memberId of partition.roster) {
      if (intent.has(memberId)) {
        continue;
      }
      const member = resolved.get(memberId);
      if (!member) {
        unresolved.push(memberId);
      } else if (ALREADY_DECIDED.has(member.status)) {
        // Satisfies the roster. Declared for validation, never re-approved.
      } else if (member.status === 'pending') {
        // Includes pending lesson drafts and appends excluded from bulk decisions above.
        unreviewed.push(memberId);
      } else {
        notApprovable.push(memberId);
      }
    }

    // Reported in the order the reviewer can act on: something he can go and read first.
    if (unreviewed.length > 0) {
      withheld.push({ label: partition.label, reason: 'unreviewed_member', workItemIds: partition.intent, blockingWorkItemIds: unreviewed });
      continue;
    }
    if (unresolved.length > 0) {
      withheld.push({ label: partition.label, reason: 'unresolved_member', workItemIds: partition.intent, blockingWorkItemIds: unresolved });
      continue;
    }
    if (notApprovable.length > 0) {
      withheld.push({ label: partition.label, reason: 'member_not_approvable', workItemIds: partition.intent, blockingWorkItemIds: notApprovable });
      continue;
    }

    groups.push({
      // The readable identity where it fits the endpoint's 128-character cap, a positional id where
      // it does not — a long namespace must not fail the whole request over a correlation label.
      groupId: partition.label.length <= MAX_WIRE_GROUP_ID_LENGTH ? partition.label : `ag-${groups.length}`,
      // The FROZEN order, not the on-screen order: the server compares sets, but a stable declared
      // order keeps the report readable and the request reproducible.
      workItemIds: partition.roster,
      approveWorkItemIds: partition.roster.filter((id) => intent.has(id)),
    });
  }

  const submitted = new Set(groups.flatMap((group) => group.workItemIds));
  const approving = new Set(groups.flatMap((group) => group.approveWorkItemIds ?? group.workItemIds));

  return {
    groups,
    withheld,
    submittedItemCount: submitted.size,
    approveItemCount: approving.size,
  };
}

/** One line per withheld group, in the reviewer's vocabulary — no ids without a reason attached. */
export function describeWithheldGroup(group: WithheldGroup): string {
  const blockers = group.blockingWorkItemIds.join(', ');
  switch (group.reason) {
    case 'unreviewed_member':
      return `${group.workItemIds.length} item(s) were not approved: they belong to an all-or-nothing group whose other member(s) are still awaiting review (${blockers}). Review those, then approve the group.`;
    case 'unresolved_member':
      return `${group.workItemIds.length} item(s) were not approved: a member of their all-or-nothing group could not be read (${blockers}), so it is not safe to assume it is already approved.`;
    case 'member_not_approvable':
      return `${group.workItemIds.length} item(s) were not approved: a member of their all-or-nothing group is rejected or already executing (${blockers}). Restore it, or reject these too.`;
    case 'external_members':
      return `${group.workItemIds.length} item(s) were not approved: their all-or-nothing group also contains an answer-key change that ships outside this tool, so the group cannot be released from here yet.`;
    case 'roster_inconsistent':
      return `${group.workItemIds.length} item(s) were not approved: the group membership frozen at import does not list them (${blockers}). This is an import defect — re-import rather than approving around it.`;
    default:
      return `${group.workItemIds.length} item(s) were not approved.`;
  }
}
